import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import toast from 'react-hot-toast';

export interface VoucherBook {
  _id: string;
  name: string;
  type: string;
  numberOfEntries: number;
  numberOfReversedEntries: number;
  lastEntryDate: string | null;
}

export function useVoucherBooks(params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['voucherBooks', params],
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const res = await api.get('/api/accounts/voucher-books', { params });
      const payload = res.data;
      if (Array.isArray(payload)) return payload as VoucherBook[];
      if (payload?.data && Array.isArray(payload.data)) return payload.data as VoucherBook[];
      return [] as VoucherBook[];
    }
  });
}


export function useCreateVoucherBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<VoucherBook>) => {
      const res = await api.post('/api/accounts/voucher-books', data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Voucher book created successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherBooks'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to create voucher book'));
    }
  });
}

export function useUpdateVoucherBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<VoucherBook> }) => {
      const res = await api.put(`/api/accounts/voucher-books/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Voucher book updated successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherBooks'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to update voucher book'));
    }
  });
}

export function useDeleteVoucherBook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/api/accounts/voucher-books/${id}`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Voucher book deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['voucherBooks'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete voucher book'));
    }
  });
}
